/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : index.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
"use strict";

import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useDispatch } from "react-redux";
import { availableColumns, attachedColumns } from "./columns";
import {
  HandleApiActions,
  legalRequirements,
} from "../../../../../redux/actions";
import ruleEngineActions from "../../../../../redux/actions/ruleEngineAction";
import CustomDataGridV2 from "../../../../../components/customComponentsV2/common/datagrid/CustomDataGridV2";

// Fixed legal scopes (source of truth)
const LEGAL_SCOPE_OPTIONS = [
  { value: "LEGAL_STANDARD", label: "Legal Standards" },
  //Provision for future extensions
  /*
  { value: "LEGAL_CHAPTERS", label: "Legal Chapters" },
  { value: "LEGAL_REQUIREMENT", label: "Legal Requirements" },
   */
];

function StepLegalScope({ ruleSetId, value = {}, onChange }) {
  const dispatch = useDispatch();

  const [selectedScope, setSelectedScope] = useState("");
  const [availableItems, setAvailableItems] = useState([]);
  const [attachedItems, setAttachedItems] = useState([]);
  const [locallyAttachedIds, setLocallyAttachedIds] = useState([]);

  const [availableLegalItemsPage, setAvailableLegalItemsPage] = useState(1);
  const [availableLegalItemsTotalCount, setAvailableLegalItemsTotalCount] =
    useState(0);
  const [attachedLegalItemsPage, setAttachedLegalItemsPage] = useState(1);
  const [attachedLegalItemsTotalCount, setAttachedLegalItemsTotalCount] =
    useState(0);

  const isScopeLocked = attachedItems.length > 0;

  const PAGE_SIZE = 5;

  const getAttachedLegalScopeItems = () => {
    const api = {
      ...ruleEngineActions.get_attached_legal_items,
      api_name: ruleEngineActions.get_attached_legal_items.api_name.replace(
        ":ruleSetId",
        ruleSetId,
      ),
    };
    dispatch(
      HandleApiActions({
        ...api,
        params: {
          page_size: PAGE_SIZE,
          page_number: attachedLegalItemsPage,
        },
        callback: (resp) => {
          if (!resp?.error) {
            const attachedItemsForDisplay = (resp.data || [])
              // 2️. Normalize for DataGrid
              .map((item) => ({
                id: item.target_id, // DataGrid identity
                standard_code: item.reference_code, // column: Standard
                name: item.display_name || "-", // column: Chapter
                description: item.description, // column: Description

                // 3️. Preserve full object for attach
                _raw: item,
              }));
            if (attachedItemsForDisplay.length > 0) {
              // Backend is authoritative for scope in edit mode - BUG expected - backend has to return scope code as LEGAL_STANDARD not STANDARD
              setSelectedScope(LEGAL_SCOPE_OPTIONS?.[0]?.value);
              setAttachedItems(attachedItemsForDisplay);
            } else {
              setSelectedScope(LEGAL_SCOPE_OPTIONS?.[0]?.value);
              setAttachedItems([]);
            }
            const nextTotalCount = Number(resp.total_count) || 0;
            const maxPage = Math.max(
              1,
              Math.ceil(nextTotalCount / PAGE_SIZE),
            );

            setAttachedLegalItemsTotalCount(nextTotalCount);

            if (attachedLegalItemsPage > maxPage) {
              setAttachedLegalItemsPage(maxPage);
            }
          }
        },
      }),
    );
  };

  const getAvailableLegalScopeItems = () => {
    dispatch(
      HandleApiActions({
        ...legalRequirements.get_legal_standard_main_details,
        params: {
          page_number: availableLegalItemsPage,
          page_size: PAGE_SIZE,
          sort_by: "ASC",
          sort_column: "created_at",
          exclude_attached_rule_set_id: ruleSetId,
        },
        show_toast: false,
        callback: (resp) => {
          if (!resp?.error) {
            const attachedIds = new Set([
              ...attachedItems.map((i) => i.id),
              ...locallyAttachedIds,
            ]);
            const availableItemsForDisplay = (resp.data || [])
              // 1️. Exclude already attached
              .filter((item) => !attachedIds.has(item.ls_id))
              // 2️. Normalize for DataGrid
              .map((item) => ({
                id: item.ls_id, // DataGrid identity
                standard_code: item.standard_number, // "Standard"
                name: item.name, // "Chapter"
                description: item.description, // "Description"

                // 3️. Preserve full object for attach
                _raw: item,
              }));

            const nextTotalCount = Number(resp.total_count) || 0;
            const maxPage = Math.max(
              1,
              Math.ceil(nextTotalCount / PAGE_SIZE),
            );

            setAvailableLegalItemsTotalCount(nextTotalCount);

            if (availableLegalItemsPage > maxPage) {
              setAvailableLegalItemsPage(maxPage);
              return;
            }

            if (
              availableItemsForDisplay.length === 0 &&
              nextTotalCount > 0 &&
              availableLegalItemsPage < maxPage
            ) {
              setAvailableLegalItemsPage((prev) => prev + 1);
              return;
            }

            setAvailableItems(availableItemsForDisplay);
          }
        },
      }),
    );
  };

  const handleAvailableLegalItemsPageChange = (page) => {
    if (page > 0) {
      setAvailableLegalItemsPage(page);
    }
  };

  const handleAttachedLegalItemsPageChange = (page) => {
    if (page > 0) {
      setAttachedLegalItemsPage(page);
    }
  };

  useEffect(() => {
    getAttachedLegalScopeItems();
  }, []);

  // ─────────────────────────────────────
  // LOAD AVAILABLE ITEMS WHEN SCOPE CHANGES
  // ─────────────────────────────────────
  useEffect(() => {
    if (!ruleSetId) return;
    if (!selectedScope) return;

    if (!availableItems) {
      // Get all available legal scope items
      getAvailableLegalScopeItems();
    }
    // If no items already then refresh to avoid multiple re-renders
    if (!attachedItems) {
      getAttachedLegalScopeItems();
    }
  }, [selectedScope]);

  // ─────────────────────────────────────
  // LOAD AVAILABLE ITEMS WHEN PAGE CHANGES
  // ─────────────────────────────────────
  useEffect(() => {
    if (!ruleSetId) return;
    getAvailableLegalScopeItems();
  }, [availableLegalItemsPage, attachedItems, locallyAttachedIds]);

  useEffect(() => {
    if (!ruleSetId) return;
    getAttachedLegalScopeItems();
  }, [attachedLegalItemsPage]);

  // ─────────────────────────────────────
  // PROPAGATE STATE TO STEPPER
  // ─────────────────────────────────────
  useEffect(() => {
    onChange?.({
      scope: selectedScope,
      attached: attachedItems,
    });
  }, [selectedScope, attachedItems]);

  // ─────────────────────────────────────
  // ATTACH
  // ─────────────────────────────────────
  const handleAttach = async (row) => {
    const api = {
      ...ruleEngineActions.attach_legal_item,
      api_name: ruleEngineActions.attach_legal_item.api_name.replace(
        ":ruleSetId",
        ruleSetId,
      ),
    };
    await dispatch(
      HandleApiActions({
        ...api,
        params: {
          linkType: selectedScope,
          targetId: row.id,
        },
        show_toast: true,
        callback: (resp) => {
          if (!resp?.error) {
            setLocallyAttachedIds((prev) =>
              prev.includes(row.id) ? prev : [...prev, row.id],
            );
            getAttachedLegalScopeItems();
            getAvailableLegalScopeItems();
          }
        },
      }),
    );
  };

  // ─────────────────────────────────────
  // DETACH
  // ─────────────────────────────────────
  const handleDetach = async (row) => {
    const api = {
      ...ruleEngineActions.detach_legal_item,
      api_name: ruleEngineActions.detach_legal_item.api_name
        .replace(":ruleSetId", ruleSetId)
        .replace(":linkType", selectedScope)
        .replace(":targetId", row.id),
    };
    await dispatch(
      HandleApiActions({
        ...api,
        show_toast: true,
        callback: (resp) => {
          if (!resp?.error) {
            setLocallyAttachedIds((prev) => prev.filter((id) => id !== row.id));
            // Always refetch available to keep ordering stable
            getAvailableLegalScopeItems();
            getAttachedLegalScopeItems();
          }
        },
      }),
    );
  };

  useEffect(() => {
    setLocallyAttachedIds([]);
  }, [ruleSetId]);

  return (
    <Box>
      <Typography
        variant="h6"
        sx={{ mb: 4, display: "flex", alignItems: "start" }}
      >
        Attach/Detach Legal Standards to Rule set
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "1fr 1fr",
            lg: "1fr 1fr",
          },
          gap: 3,
          minHeight: 500,
          height: 500,
        }}
      >
        {/* AVAILABLE */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            minWidth: 0,
          }}
        >
          <Typography sx={{ mb: 1 }}>Available</Typography>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              overflow: "hidden",
              maxHeight: "370px",
            }}
          >
            <CustomDataGridV2
              rows={availableItems}
              columns={availableColumns(handleAttach)}
              hideHeader={false}
              rowsPerPage={5}
              page={availableLegalItemsPage}
              showPagination={true}
              handlePageChange={(page) => {
                handleAvailableLegalItemsPageChange(page);
              }}
              totalItems={availableLegalItemsTotalCount}
            />
          </Box>
        </Box>

        {/* ATTACHED */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            minWidth: 0,
          }}
        >
          <Typography sx={{ mb: 1 }}>Attached</Typography>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              overflow: "hidden",
              maxHeight: "370px",
            }}
          >
            <CustomDataGridV2
              rows={attachedItems}
              columns={attachedColumns(handleDetach)}
              hideHeader={false}
              rowsPerPage={5}
              page={attachedLegalItemsPage}
              showPagination={true}
              totalItems={attachedLegalItemsTotalCount}
              handlePageChange={(page) => {
                handleAttachedLegalItemsPageChange(page);
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default StepLegalScope;
